import axios from 'axios';
import { api } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const BASE_URL = `${API_URL}/api`;

export interface CalendarConferenceEntryPoint {
    entryPointType?: string;
    uri?: string;
}

export interface CalendarEvent {
    id: string;
    summary: string;
    description?: string;
    location?: string;
    start: {
        dateTime?: string;
        date?: string;
    };
    end: {
        dateTime?: string;
        date?: string;
    };
    htmlLink: string;
    hangoutLink?: string;
    conferenceData?: {
        entryPoints?: CalendarConferenceEntryPoint[];
    };
    calendar_id?: string;
    /** IANA zone of the source calendar (e.g. America/Bogota). */
    calendar_time_zone?: string | null;
    /** Reminder progress from calendar_messages; null when the event has no reminders. */
    reminder_status?: CalendarReminderStatus | null;
}

export interface CalendarReminderStatus {
    /** Messages no longer pending (sent, failed or blocked). */
    sent: number;
    total: number;
    /** Next pending send (ISO UTC). */
    next_at: string | null;
}

export interface CalendarListEntry {
    id: string;
    summary: string;
    description?: string;
    primary?: boolean;
    backgroundColor?: string;
    foregroundColor?: string;
    is_active?: boolean;
    updated?: string;
}

export interface CalendarCampaignCreate {
    name: string;
    calendar_id: string;
    template_id?: string;
    message_template: string;
    calendar_config: {
        calendar_id: string;
        timezone?: string;
        reminder_days_before?: number[];
        reminder_hours_before?: number[];
    };
    column_mapping?: Record<string, string>;
    country_code?: string;
    is_new_template?: boolean;
}

export interface CalendarCampaign {
    id: string;
    user_id: string;
    name: string;
    calendar_id: string;
    country_code: string;
    message_template: string;
    column_mapping?: Record<string, string>;
    calendar_config: Record<string, any>;
    template_id?: string;
    whatsapp_template_name?: string;
    template_status?: string;
    status: string;
    created_at: string;
    updated_at: string;
    total_recipients: number;
    messages_sent: number;
    messages_failed: number;
}

type FetchOptions = { force?: boolean };

/** In-flight dedupe so StrictMode / parallel mounts share one network request. */
function dedupeInflight<T>(
    slot: { current: Promise<T> | null },
    force: boolean | undefined,
    factory: () => Promise<T>
): Promise<T> {
    if (!force && slot.current) return slot.current;
    const promise = factory().finally(() => {
        if (slot.current === promise) slot.current = null;
    });
    slot.current = promise;
    return promise;
}

const listCalendarsInflight: { current: Promise<CalendarListEntry[]> | null } = { current: null };
const allEventsInflight: { current: Promise<CalendarEvent[]> | null } = { current: null };
const campaignsInflight: { current: Promise<CalendarCampaign[]> | null } = { current: null };

class CalendarService {
    async getEvents(calendarId: string = 'primary'): Promise<CalendarEvent[]> {
        try {
            const response = await axios.get(`${BASE_URL}/calendar/events`, {
                params: { calendar_id: calendarId },
                withCredentials: true
            });
            return response.data.events;
        } catch (error) {
            console.error("Error fetching calendar events:", error);
            throw error;
        }
    }

    async listCalendars(options?: FetchOptions): Promise<CalendarListEntry[]> {
        return dedupeInflight(listCalendarsInflight, options?.force, async () => {
            try {
                const response = await axios.get(`${BASE_URL}/calendar/calendars`, {
                    withCredentials: true
                });
                return response.data.calendars;
            } catch (error) {
                console.error("Error listing calendars:", error);
                throw error;
            }
        });
    }

    async createEvent(eventData: any, calendarId: string = 'primary'): Promise<CalendarEvent> {
        try {
            const response = await axios.post(`${BASE_URL}/calendar/events`, eventData, {
                params: { calendar_id: calendarId },
                withCredentials: true
            });
            return response.data;
        } catch (error) {
            console.error("Error creating event:", error);
            throw error;
        }
    }

    async createCalendarCampaign(data: CalendarCampaignCreate): Promise<CalendarCampaign> {
        const response = await api.post('/calendar-campaigns', data);
        return response.data;
    }

    async getAllActiveCalendarEvents(options?: FetchOptions): Promise<CalendarEvent[]> {
        return dedupeInflight(allEventsInflight, options?.force, async () => {
            try {
                const response = await axios.get(`${BASE_URL}/calendar/events/all`, {
                    withCredentials: true
                });
                return response.data.events;
            } catch (error) {
                console.error("Error fetching all calendar events:", error);
                throw error;
            }
        });
    }

    async getCalendarCampaigns(options?: FetchOptions): Promise<CalendarCampaign[]> {
        return dedupeInflight(campaignsInflight, options?.force, async () => {
            const response = await api.get('/calendar-campaigns');
            return response.data;
        });
    }

    async cancelCalendarCampaign(campaignId: string): Promise<void> {
        await api.post(`/calendar-campaigns/${campaignId}/cancel`);
    }

    /** Disconnect Calendar OAuth only; does not affect Google Sheets. */
    async resetCalendarAuth(): Promise<void> {
        await api.delete('/calendar/reset-auth');
    }
}

export const calendarService = new CalendarService();
